import type { ChatRevertItem } from "schemas/generated/chatMessage";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useFetchPageFilesForWebContainer } from "@/features/web-container/useFetchPageFilesForWebContainer";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import {
  Card,
  CardContent,
} from "@replo/design-system/components/shadcn/core/card";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { CircleX, MoreHorizontal, RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { formatDateToMonthDayHour } from "replo-utils/lib/datetime";

import {
  useChatStoreActions,
  useViewingVersionId,
} from "../store/ChatStoreContext";

interface ChangeBlockProps {
  isLoading?: boolean;
  appliedDiffs?: string[];
  fileVersionId?: string;
}

// Simple easing function - starts fast, slows down at the end
const easeOut = (t: number): number => {
  return 1 - Math.pow(1 - t, 2);
};

const useSimulatedProgress = (
  isActive: boolean,
  targetDuration: number = 120000,
  updatesPerSecond: number = 1,
) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousProgressRef = useRef(0);

  const updateInterval = 1000 / updatesPerSecond;

  const updateProgress = useCallback(() => {
    if (!startTimeRef.current || !isActive) {
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;
    const normalizedTime = Math.min(elapsed / targetDuration, 1);

    const easedProgress = easeOut(normalizedTime);
    const randomBump = Math.random() * 0.02;
    const calculatedProgress = Math.min((easedProgress + randomBump) * 95, 95);

    const newProgress = Math.max(
      calculatedProgress,
      previousProgressRef.current,
    );

    previousProgressRef.current = newProgress;
    setProgress(newProgress);

    if (normalizedTime >= 1 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isActive, targetDuration]);

  useEffect(() => {
    if (isActive && !startTimeRef.current) {
      startTimeRef.current = Date.now();
      previousProgressRef.current = 0;
      setProgress(0);

      setTimeout(() => {
        if (isActive) {
          intervalRef.current = setInterval(updateProgress, updateInterval);
        }
      }, 100);
    } else if (!isActive) {
      startTimeRef.current = null;
      previousProgressRef.current = 0;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, updateProgress, updateInterval]);

  return progress;
};

const ChangeBlock = ({
  isLoading,
  appliedDiffs,
  fileVersionId,
}: ChangeBlockProps) => {
  const isDebug = useIsDebugMode();
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const { setViewingVersionId } = useChatStoreActions();
  const viewingVersionId = useViewingVersionId();

  const [previewLoading, setPreviewLoading] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);

  const { data: fileVersion, isLoading: isFileVersionLoading } =
    trpc.fileVersion.get.useQuery(
      fileVersionId && projectId
        ? { id: fileVersionId, projectId, shouldIncludeDeleted: true }
        : skipToken,
    );

  const { data: fileVersionsData } =
    trpc.fileVersion.findByFileId.useInfiniteQuery(
      projectId && pageId
        ? {
            projectId,
            fileId: pageId,
            limit: 1,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  const fileVersions = React.useMemo(
    () => fileVersionsData?.pages?.flatMap((page) => page.fileVersions) ?? [],
    [fileVersionsData],
  );

  const currentVersion = fileVersions?.[0];
  const isCurrentVersion = currentVersion?.id === fileVersionId;

  const simulatedProgress = useSimulatedProgress(Boolean(isLoading));

  // NOTE (Bobby: 2025-07-23): Managing the showLoadingState logic ourselves since we want to manually add a delay and fill out the progress bar ourselves when the
  // actual loading state is finished. This is to give that bit of buffer for the progress bar to fill out instead of just flashing to the finished state.
  useEffect(() => {
    if (isLoading) {
      setShowLoadingState(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && showLoadingState) {
      setTimeout(() => {
        setShowLoadingState(false);
      }, 800);
    }
  }, [isLoading, showLoadingState]);

  const fetchPageFiles = useFetchPageFilesForWebContainer();

  const {
    mutateAsync: revertPageToVersion,
    isPending: isRevertingPageToVersion,
  } = trpc.nextJSProject.revertPageToVersion.useMutation({
    onSuccess: () => {
      void fetchPageFiles([{ id: pageId! }]);
      if (projectId && pageId) {
        void trpcUtils.fileVersion.findByFileId.invalidate({
          projectId,
          fileId: pageId,
        });
        void trpcUtils.fileVersion.invalidate();
      }
    },
  });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  const handleRevertPageToVersion = useCallback(async () => {
    if (projectId && fileVersionId && user?.id && chatId) {
      const newVersion = await revertPageToVersion({
        projectId,
        fileVersionId,
        userId: user?.id,
      });

      const revertItem: ChatRevertItem = {
        fileVersionId: newVersion.id,
        context: "User reverted to this version through the UI",
      };
      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "revert" as const,
            text: JSON.stringify(revertItem),
            isNewMessageGroup: true,
          },
        ],
        role: "assistant",
      });

      void trpcUtils.chat.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void trpcUtils.chat.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
      setViewingVersionId(newVersion.id);
    }
  }, [
    projectId,
    fileVersionId,
    user?.id,
    revertPageToVersion,
    chatId,
    addMessage,
    setViewingVersionId,
  ]);

  const handlePreviewVersion = useCallback(async () => {
    if (!projectId || !pageId || !fileVersionId) {
      return;
    }

    setPreviewLoading(true);

    await fetchPageFiles([{ id: pageId!, fileVersionId }]);
    setViewingVersionId(fileVersionId);
    setPreviewLoading(false);
  }, [projectId, pageId, fileVersionId, setViewingVersionId, fetchPageFiles]);

  if (showLoadingState) {
    const displayProgress = isLoading ? simulatedProgress : 100;

    return (
      <Card
        className="w-full max-w-[400px] p-0"
        style={{
          background:
            "var(--Gradient, linear-gradient(90deg, rgba(239, 25, 110, 0.20) 0%, rgba(255, 189, 48, 0.20) 50%, rgba(39, 74, 226, 0.20) 100%))",
        }}
      >
        <CardContent className="flex flex-col items-start gap-3 p-4">
          <div className="text-base font-semibold">Updating Page</div>
          <div className="text-sm text-muted relative">
            <span className="relative">
              Making changes to your page...
              <span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent bg-clip-text text-transparent animate-[textShimmer_4s_ease-in-out_infinite]"
                style={{
                  backgroundSize: "200% 100%",
                  mixBlendMode: "overlay",
                }}
              >
                Making changes to your page...
              </span>
            </span>
          </div>

          <div className="w-full flex gap-3 items-center">
            <Progress
              value={displayProgress}
              className="h-2 bg-white [&>div]:bg-black [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out"
            />
          </div>
        </CardContent>

        <style>{`
        @keyframes textShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      </Card>
    );
  }

  if (!fileVersion) {
    if (isFileVersionLoading) {
      return (
        <Card className={twMerge("w-full max-w-[400px] p-0")}>
          <CardContent className="flex flex-col items-start gap-2 p-4">
            <div className="flex flex-row gap-3 items-start">
              <div className="flex flex-col gap-1">
                <div className="text-base font-semibold">Loading...</div>
                <div className="text-sm text-muted relative pb-8">
                  Loading the version...
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className={twMerge("w-full max-w-[400px] p-0")}>
        <CardContent className="flex flex-col items-start gap-2 p-4">
          <div className="flex flex-row gap-3 items-start">
            <IconButton
              variant="secondary"
              UNSAFE_className="bg-danger-soft border-danger/50"
              icon={<CircleX size={16} className="text-danger" />}
            />
            <div className="flex flex-col gap-1">
              <div className="text-base font-semibold">
                We couldn't make that change
              </div>
              <div className="text-sm text-muted relative">
                Something went wrong while applying your edit. Please try again
                or reach out to us at support@replo.com
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isViewingVersion = viewingVersionId === fileVersionId;

  return (
    <Card
      className={twMerge(
        "w-full max-w-[400px] p-0",
        fileVersionId === viewingVersionId &&
          "bg-sheet border-2 border-default",
        fileVersion.deletedAt && "opacity-50",
      )}
    >
      <CardContent className="flex flex-col items-start gap-2 p-4">
        <div className="text-base font-semibold">{fileVersion.summary}</div>
        <div className="text-sm text-muted relative">
          {isCurrentVersion
            ? "Current Version"
            : `${formatDateToMonthDayHour(fileVersion.createdAt)}`}
        </div>

        <div className="flex flex-row w-full gap-2 justify-end">
          {isDebug && appliedDiffs && appliedDiffs.length > 0 && (
            <Popover
              triggerAsChild
              shouldPreventDefaultOnInteractOutside={false}
              side="bottom"
              align="start"
              sideOffset={8}
              className="w-[500px]"
              title="Manage Changes"
              content={
                <div className="max-h-96 overflow-auto">
                  <div className="text-sm">File version: {fileVersionId}</div>
                  <div className="text-sm">
                    File version URL: {fileVersion.url}
                  </div>
                  {appliedDiffs.map((diff, index) => (
                    <pre
                      key={index}
                      className="text-xs bg-neutral-soft p-2 rounded border mb-2 whitespace-pre-wrap"
                    >
                      {diff}
                    </pre>
                  ))}
                </div>
              }
            >
              <IconButton
                variant="secondary"
                icon={<MoreHorizontal className="size-4" />}
                tooltipText="View debug diffs"
                size="sm"
              />
            </Popover>
          )}

          {!fileVersion.deletedAt && (
            <div className="flex flex-row w-full gap-2 justify-end">
              {!isCurrentVersion && (
                <Button
                  onClick={() => {
                    void handleRevertPageToVersion();
                  }}
                  isLoading={isRevertingPageToVersion}
                  variant="tertiary"
                >
                  <div className="flex items-center gap-1">
                    <RefreshCw strokeWidth={3} className="size-3" /> Restore
                  </div>
                </Button>
              )}
              <Button
                disabled={isViewingVersion}
                onClick={() => {
                  void handlePreviewVersion();
                }}
                isLoading={previewLoading}
                variant="secondary"
              >
                {isViewingVersion ? "Viewing" : "Preview"}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChangeBlock;
